import { BusinessUnit } from '#models/business-unit';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { BusinessUnitsService } from '#services/api';
import { ToastrService } from '#services/shared/toastr.service';
import { PaginationSettings } from '#models/pagination';
import { ChecklistService } from 'functional-review/services/api/checklist.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { HttpErrorFormat } from 'functional-review/models/http-error-format';
import { FunctionalReviewSharedHelper } from 'functional-review/services/helpers/functional-review-shared.helper';
import { TranslatePipe } from '@ngx-translate/core';
import { ImportCheckListDialogComponent } from './import-checklist-dialog/import-checklist-dialog.component';
import { exhaustMap, filter, share, switchMap, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';



@Component({
  selector: 'app-checklists',
  templateUrl: './checklists.component.html',
  styleUrls: ['./checklists.component.scss'],
  providers: [TranslatePipe]
})
export class CheckListsComponent implements OnInit, OnDestroy {

  businessUnits: BusinessUnit[] = [];
  selectedBusinessUnit: BusinessUnit | null = null;
  mocId = '';
  taskPlans = [];
  pagination = new PaginationSettings(20);

  private subscription = new Subject();

  checkListTypes: string[] = [];

  constructor(
    private checklistService: ChecklistService,
    private businesUnitSvc: BusinessUnitsService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmationService: ConfirmationService,
    private functionalReviewSharedHelper: FunctionalReviewSharedHelper,
    public dialog: MatDialog,
    private translatePipe: TranslatePipe

  ) { }


  loadBusinessUnits() {

    this.businesUnitSvc.getUserBUs()
      .pipe(
        takeUntil(this.subscription)
      ).subscribe(data => {
        this.businessUnits = data;

        const currentBusinessUnit = this.getCurrentBusinessUnit(data);

        this.selectBusinessUnit(currentBusinessUnit);
      }, ({ error }: HttpErrorFormat) => {
        const { details, summary } = this.functionalReviewSharedHelper.getWebError({ error });

        this.toastrService.showError(details, summary);
      });

  }

  private getCurrentBusinessUnit(data: BusinessUnit[]) {
    const paramBusinessUnitId = +this.route.snapshot.params.businessUnitId;

    if (!isNaN(paramBusinessUnitId)) {
      const result = data.find(d => d.Id === paramBusinessUnitId);

      return 'undefined' === typeof result ? null : result;
    }

    if (data.length > 0) {
      return data[0];
    }

    return null;
  }

  selectBusinessUnit(data: BusinessUnit) {
    this.selectedBusinessUnit = data;

    if (this.selectedBusinessUnit !== null) {
      this.pagination.reset();

      this.loadChecklists(this.selectedBusinessUnit.Id);

      this.router.navigate(['/functional-review/checklists', {
        businessUnitId: this.selectedBusinessUnit.Id
      }], {
        replaceUrl: false,
        queryParamsHandling: 'merge'
      });
    }
  }

  loadChecklists(businessUnitId: number) {

    const getChecklist$ = this.checklistService.getChecklist({
      businessUnitId
    }, {
      PageNumber: this.pagination.pageNumber,
      PageSize: this.pagination.pageSize
    })
      .pipe(
        share(),
        takeUntil(this.subscription)
      );

    getChecklist$.subscribe(
      (data) => {
        this.pagination.updateCounters(data.Items.length, data.TotalCount);
        this.taskPlans = data.Items;
      },
      ({ error }: HttpErrorFormat) => {
        const { details, summary } = this.functionalReviewSharedHelper.getWebError({ error });

        this.toastrService.showError(details, summary);
      });

    return getChecklist$;
  }

  ngOnInit() {
    this.loadBusinessUnits();
    this.loadCheckListTypes();

    this.mocId = sessionStorage.getItem('mocId');
  }

  onPageChange(pageNumber: number) {
    this.pagination.pageNumber = pageNumber;

    this.loadChecklists(this.selectedBusinessUnit.Id);
  }

  deleteTaskPlan(taskPlanId: number) {
    this.confirmationService.confirm({
      message: this.translatePipe.transform('FR.Dialogs.Description.Delete'),
      header: this.translatePipe.transform('FR.Dialogs.Title.Delete'),
      accept: () => {
        const taskPlan$ = this.checklistService.deleteTaskPlan(taskPlanId)
          .pipe(takeUntil(this.subscription));


        taskPlan$.subscribe(() => {
          this.loadChecklists(this.selectedBusinessUnit.Id);
        });

      }
    });
  }


  ngOnDestroy() {
    this.subscription.next();
    this.subscription.complete();
  }

  importChecklist() {
    const data: SelectItem[] =
      [{
        label: '',
        value: ''
      },
      ...this.checkListTypes.map<SelectItem>(checkListType => ({
        value: checkListType,
        label: checkListType
      }))];

    const dialogRef = this.dialog.open(
      ImportCheckListDialogComponent, {
      hasBackdrop: true,
      width: '400px',
      // height: '400px',
      panelClass: 'import-checklist-dialog',
      data

    });

    dialogRef.afterClosed()
      .pipe(
        filter((_) => typeof _ !== 'undefined'),
        switchMap((checklistType) => this.checklistService.importChecklist(
          {
            businessUnitId: this.selectedBusinessUnit.Id,
            checklistType
          }
        )),

        exhaustMap(() =>
          this.loadChecklists(this.selectedBusinessUnit.Id)
        ),
        takeUntil(this.subscription)
      )
      .subscribe(() => {
        this.toastrService.showSuccess(this.translatePipe.transform('FR.Messages.ChecklistImported'));
      },
        ({ error }: HttpErrorFormat) => {
          const { details, summary } = this.functionalReviewSharedHelper.getWebError({ error });

          this.toastrService.showError(details, summary);
        }
      );


  }

  loadCheckListTypes() {
    this.checklistService.getCheckListTypes()
      .pipe(takeUntil(this.subscription))

      .subscribe(
        (checkListTypes = []) => {
          this.checkListTypes = checkListTypes;
        }
      );
  }
}
